import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import EmployeeFormModal from '../components/EmployeeFormModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import './EmployeeList.css';
import { toast } from '../components/Toast';

function EmployeeList() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const [sortConfig, setSortConfig] = useState({ key: 'employeeCode', direction: 'asc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedEmployees = React.useMemo(() => {
    let sortable = [...filteredEmployees];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'name') {
          aVal = a.userName || a.name || '';
          bVal = b.userName || b.name || '';
        } else if (sortConfig.key === 'email') {
          aVal = a.userEmail || a.email || '';
          bVal = b.userEmail || b.email || '';
        } else if (sortConfig.key === 'role') {
          aVal = a.customRoleName || a.userRole || '';
          bVal = b.customRoleName || b.userRole || '';
        } else if (sortConfig.key === 'joinDate') {
          aVal = new Date(a.joinDate).getTime();
          bVal = new Date(b.joinDate).getTime();
        }

        if (aVal === undefined || aVal === null) aVal = '';
        if (bVal === undefined || bVal === null) bVal = '';

        if (typeof aVal === 'string') {
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        } else {
          return sortConfig.direction === 'asc'
            ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0)
            : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
        }
      });
    }
    return sortable;
  }, [filteredEmployees, sortConfig]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchTerm, departmentFilter, statusFilter, roleFilter]);

  const fetchEmployees = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/employees');
      const employeeData = response.data.employees || response.data.data?.employees || [];
      setEmployees(employeeData);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  const filterEmployees = () => {
    let filtered = [...employees];

    if (searchTerm) {
      filtered = filtered.filter(employee => {
        const name = (employee.userName || employee.name || '').toLowerCase();
        const email = (employee.userEmail || employee.email || '').toLowerCase();
        const code = (employee.employeeCode || '').toLowerCase();
        const designation = (employee.designation || '').toLowerCase();
        const term = searchTerm.toLowerCase();
        return name.includes(term) || email.includes(term) || code.includes(term) || designation.includes(term);
      });
    }

    if (departmentFilter) {
      filtered = filtered.filter(employee => employee.department === departmentFilter);
    }

    if (statusFilter) {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter(employee => employee.active === isActive);
    }

    if (roleFilter) {
      filtered = filtered.filter(employee => (employee.customRoleName || employee.userRole) === roleFilter);
    }

    setFilteredEmployees(filtered);
    setCurrentPage(0);
  };

  const handleCreate = () => {
    setSelectedEmployee(null);
    setShowModal(true);
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setShowModal(true);
  };

  const handleView = (employee) => {
    navigate(`/employees/${employee.id}`);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedEmployee(null);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setSelectedEmployee(null);
    fetchEmployees();
    showSuccessMessage('Employee saved successfully');
  };

  const handleDelete = (employee) => {
    setEmployeeToDelete(employee);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!employeeToDelete) return;
    try {
      await axios.delete(`/employees/${employeeToDelete.id}`);
      setShowConfirmModal(false);
      setEmployeeToDelete(null);
      fetchEmployees();
      showSuccessMessage('Employee deleted successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete employee');
      setShowConfirmModal(false);
      setEmployeeToDelete(null);
    }
  };

  const showSuccessMessage = (message) => {
    toast.success(message);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('');
    setStatusFilter('');
    setRoleFilter('');
  };

  return (
    <div className="employee-list-container">
      <div className="page-header">
        <h1>Employees</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          Add Employee
        </button>
      </div>

      <div className="filters-section">
        <div className="emp-filter-row">
          <label className="emp-filter-label">Search</label>
          <input
            type="text"
            placeholder="Search by name, code, email, or designation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="emp-filter-row">
          <label className="emp-filter-label">Role</label>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="filter-select">
            <option value="">All Roles</option>
            <option value="COMPANY_ADMIN">Company Admin</option>
            <option value="BRANCH_MANAGER">Branch Manager</option>
            <option value="SUPERVISOR">Supervisor</option>
            <option value="EMPLOYEE">Employee</option>
          </select>
        </div>

        <div className="emp-filter-row">
          <label className="emp-filter-label">Department</label>
          <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} className="filter-select">
            <option value="">All Departments</option>
            <option value="IT">IT</option>
            <option value="HR">HR</option>
            <option value="Finance">Finance</option>
            <option value="Operations">Operations</option>
            <option value="Construction">Construction</option>
          </select>
        </div>

        <div className="emp-filter-row">
          <label className="emp-filter-label">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <button className="btn btn-secondary btn-sm emp-clear-btn" onClick={clearFilters}>
          Clear Filters
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading employees...</div>
      ) : (
        <div className="table-container">
          <table className="employee-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('employeeCode')} style={{ cursor: 'pointer', userSelect: 'none' }}>Employee Code {getSortIcon('employeeCode')}</th>
                <th onClick={() => requestSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Name {getSortIcon('name')}</th>
                <th onClick={() => requestSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>Email {getSortIcon('email')}</th>
                <th onClick={() => requestSort('designation')} style={{ cursor: 'pointer', userSelect: 'none' }}>Designation {getSortIcon('designation')}</th>
                <th onClick={() => requestSort('role')} style={{ cursor: 'pointer', userSelect: 'none' }}>Role {getSortIcon('role')}</th>
                <th onClick={() => requestSort('joinDate')} style={{ cursor: 'pointer', userSelect: 'none' }}>Join Date {getSortIcon('joinDate')}</th>
                <th onClick={() => requestSort('hasLogin')} style={{ cursor: 'pointer', userSelect: 'none' }}>Login {getSortIcon('hasLogin')}</th>
                <th onClick={() => requestSort('active')} style={{ cursor: 'pointer', userSelect: 'none' }}>Status {getSortIcon('active')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedEmployees.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-data">No employees found</td>
                </tr>
              ) : (
                sortedEmployees.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage).map((employee) => (
                  <tr key={employee.id}>
                    <td data-label="Code"><strong>{employee.employeeCode}</strong></td>
                    <td data-label="Name">{employee.userName || employee.name}</td>
                    <td data-label="Email">{employee.userEmail || employee.email || '-'}</td>
                    <td data-label="Designation">{employee.designation || '-'}</td>
                    <td data-label="Role">{employee.customRoleName || (employee.userRole ? employee.userRole.replace('_', ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : '-')}</td>
                    <td data-label="Join Date">{new Date(employee.joinDate).toLocaleDateString()}</td>
                    <td data-label="Login Enabled">
                      <span className={`status-badge ${employee.hasLogin ? 'active' : 'inactive'}`}>
                        {employee.hasLogin ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td data-label="Status">
                      <span className={`status-badge ${employee.active ? 'active' : 'inactive'}`}>
                        {employee.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td data-label="Actions">
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleView(employee)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(employee)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(employee)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {Math.ceil(filteredEmployees.length / itemsPerPage) > 1 && (
            <div className="pagination" style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
              <button
                className="btn btn-sm"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage + 1} of {Math.ceil(filteredEmployees.length / itemsPerPage)} ({filteredEmployees.length} total)
              </span>
              <button
                className="btn btn-sm"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredEmployees.length / itemsPerPage) - 1, prev + 1))}
                disabled={currentPage >= Math.ceil(filteredEmployees.length / itemsPerPage) - 1}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <EmployeeFormModal
          employee={selectedEmployee}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      <DeleteConfirmModal
        isOpen={showConfirmModal}
        title="Delete Employee"
        message={<>Are you sure you want to delete employee <strong>{employeeToDelete?.userName}</strong>?</>}
        onCancel={() => { setShowConfirmModal(false); setEmployeeToDelete(null); }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

export default EmployeeList;
